/**
 * frontend/src/components/PermissionGate.jsx
 *
 * Declarative permission gating — wraps any UI element.
 * Two variants:
 *
 * <ProjectPermissionGate projectId={id} permission="create_tasks">
 *   <button>New Task</button>
 * </ProjectPermissionGate>
 * → renders children only if the user has that permission on that project
 *
 * <SystemRoleGate roles={['Admin','Manager']}>
 *   <button>New Project</button>
 * </SystemRoleGate>
 * → renders children only if user's system role is in the list
 *
 * Both support a `fallback` prop for showing something when access is denied:
 * <ProjectPermissionGate projectId={id} permission="view_financials"
 *   fallback={<p>Budget hidden — contact your Delivery Manager</p>}>
 *   <BudgetCard />
 * </ProjectPermissionGate>
 */
import { useProjectPermissions } from '../hooks/usePermissions';
import { useAuth } from '../hooks/useAuth';
import { matchesLevels } from '../utils/rolesConfig';

// ── Project-level gate ─────────────────────────────────────────────────────
export function ProjectPermissionGate({ projectId, permission, children, fallback = null }) {
  const { can, loading } = useProjectPermissions(projectId);
  // While loading, render nothing (avoids flash of action buttons)
  if (loading) return null;
  return can(permission) ? children : fallback;
}

// ── System role gate ───────────────────────────────────────────────────────
export function SystemRoleGate({ roles, children, fallback = null }) {
  const { user } = useAuth();
  if (!user) return null;
  // Levels, not role values: 'Administrator' resolves to SystemAdmin.
  return matchesLevels(user.role, roles) ? children : fallback;
}

// ── Combined gate: system role OR project permission ───────────────────────
// Useful for "show if Manager/Admin OR if user has manage_risks project role"
export function AnyPermissionGate({ projectId, permission, systemRoles = [], children, fallback = null }) {
  const { can, loading } = useProjectPermissions(projectId);
  const { user } = useAuth();
  if (loading) return null;
  const systemOk  = matchesLevels(user?.role, systemRoles);
  const projectOk = permission ? can(permission) : false;
  return (systemOk || projectOk) ? children : fallback;
}
